import { getChannelAPI } from '@/chat/domain/ChannelApiAdapter'
import { formatMessage } from '@/shared/utils/FormatMessageUtil'
import { ChatMessagePostBodyDto } from '@/chat/dtos/ChatMessagePostBodyDto'
import { ChatGroup } from '@/chat/domain/ChatGroup'
import { getHawkeyeApi } from '@/shared/utils/getHawkeyeApi'
import type { ChannelTypes } from '../domain/ChannelTypes'
import { getTokenFromUrl } from '@/shared/utils/getTokenFromUrl'

const getHeaders = (apiKey = '') => {
  return new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'access-token': sessionStorage.getItem('IL_chat_token') as string,
    'x-api-key': apiKey || getTokenFromUrl()
  })
}

export interface ChatRepository {
  createChatMessage: (
    chatGroup: ChatGroup,
    message: string,
    participantId: string,
    orderId: string,
    fileUrl?: string | null
  ) => Promise<void>
  getUrlOfFile: (file: File, orderId: string) => Promise<string | undefined>
  putOnline: (
    jobId: string,
    particpantId: string,
    particpantName: string
  ) => Promise<void>
  signedImage: (imageUrl: string) => Promise<string[]>
  updatedSuggestion: (
    finalMessageSent: string,
    suggestionId: string,
    senderId: string,
    senderAction: string,
    jobId: string,
    channel: ChannelTypes
  ) => Promise<boolean | undefined>
  getAccessToken: (jobId: string, apiKey: string) => Promise<string | undefined>
}

export class ChatRepositoryImpl implements ChatRepository {
  public async createChatMessage(
    chatGroup: ChatGroup,
    message: string,
    participantId: string,
    orderId: string,
    fileUrl?: string | null
  ): Promise<void> {
    try {
      const body = JSON.stringify(
        new ChatMessagePostBodyDto(
          participantId,
          getChannelAPI(chatGroup),
          message,
          fileUrl ? [fileUrl] : []
        )
      )
      const options = {
        method: 'POST',
        headers: getHeaders(),
        body
      }

      await fetch(`${getHawkeyeApi()}/chat/v2/${orderId}/messages`, options)
    } catch (error) {
      console.error(
        formatMessage(
          ChatRepositoryImpl.name,
          this.createChatMessage.name,
          error
        )
      )
    }
  }

  public async getUrlOfFile(
    file: File,
    orderId: string
  ): Promise<string | undefined> {
    try {
      const headers = new Headers({
        'access-token': sessionStorage.getItem('IL_chat_token') as string,
        'x-api-key': getTokenFromUrl()
      })

      const body = new FormData()
      body.append('file', file)

      const options = {
        method: 'POST',
        body,
        headers
      }

      const request = await fetch(
        `${getHawkeyeApi()}/chat/v2/${orderId}/files`,
        options
      )

      const response = await request.json()
      if (response.success) {
        return response.url
      } else {
        throw new Error('Error: api cant upload file')
      }
    } catch (error) {
      console.error(
        formatMessage(ChatRepositoryImpl.name, this.getUrlOfFile.name, error)
      )
    }
  }

  public async putOnline(
    jobId: string,
    particpantId: string,
    particpantName: string
  ): Promise<void> {
    try {
      const body = JSON.stringify({
        channelType: getChannelAPI(ChatGroup.SUPPORT),
        type: 'SUP',
        name: particpantName
      })

      const options = {
        method: 'PUT',
        headers: getHeaders(),
        body
      }

      await fetch(
        `${getHawkeyeApi()}/chat/v2/${jobId}/participants/${particpantId}/online`,
        options
      )
    } catch (error) {
      console.error(
        formatMessage(ChatRepositoryImpl.name, this.putOnline.name, error)
      )
    }
  }

  public async signedImage(imageUrl: string): Promise<string[]> {
    try {
      const request = await fetch(
        `${getHawkeyeApi()}/chat/v2/files/signed-urls`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ urls: [imageUrl] })
        }
      )
      return await request.json()
    } catch (error) {
      console.error(
        formatMessage(ChatRepositoryImpl.name, this.signedImage.name, error)
      )
    }
    return []
  }

  public async updatedSuggestion(
    finalMessageSent: string,
    suggestionId: string,
    senderId: string,
    senderAction: string,
    jobId: string,
    channel: ChannelTypes
  ): Promise<boolean | undefined> {
    try {
      const body = JSON.stringify({
        channel,
        finalMessageSent,
        senderAction,
        senderId
      })

      const options = {
        method: 'PUT',
        headers: getHeaders(),
        body
      }

      await fetch(
        `${getHawkeyeApi()}/chat/v2/${jobId}/suggestions/${suggestionId}`,
        options
      )
      return true
    } catch (error) {
      console.error(
        formatMessage(
          ChatRepositoryImpl.name,
          this.updatedSuggestion.name,
          error
        )
      )
    }
  }

  public async getAccessToken(
    jobId: string,
    apiKey: string
  ): Promise<string | undefined> {
    try {
      const options = {
        method: 'GET',
        headers: getHeaders(apiKey)
      }

      const response = await fetch(
        `${getHawkeyeApi()}/chat/v2/${jobId}/token`,
        options
      )
      const { token } = await response.json()
      return token
    } catch (error) {
      console.error(
        formatMessage(ChatRepositoryImpl.name, this.getAccessToken.name, error)
      )
    }
  }
}
